import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * Bloques de skeleton reutilizables para pantallas de carga.
 * Reemplazan el spinner crudo con una silueta del contenido real,
 * reduciendo el salto de layout percibido y elevando la sensación premium.
 * Todos respetan el acento del tema vía bg-primary/10 (Skeleton base).
 */

// Encabezado de página: título grande + subtítulo.
export const SkeletonHeader = ({ className }) => (
  <div className={cn('space-y-2', className)}>
    <Skeleton className="h-9 w-48" />
    <Skeleton className="h-4 w-64" />
  </div>
);

// Tarjeta KPI (misma silueta que .kpi-card del dashboard).
export const SkeletonStat = ({ className }) => (
  <div className={cn('kpi-card', className)}>
    <div className="flex items-center justify-between mb-3">
      <Skeleton className="h-5 w-5 rounded" />
    </div>
    <Skeleton className="h-9 w-20 mb-2" />
    <Skeleton className="h-3 w-16" />
  </div>
);

// Fila de estadísticas (grid de KPIs).
export const SkeletonStatsRow = ({ count = 4, className }) => (
  <div className={cn('grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4', className)}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonStat key={i} />
    ))}
  </div>
);

// Tarjeta genérica con encabezado + cuerpo.
export const SkeletonCard = ({ className, rows = 3 }) => (
  <div className={cn('card-default p-6 space-y-4', className)}>
    <div className="flex items-center justify-between">
      <Skeleton className="h-5 w-32" />
      <Skeleton className="h-4 w-16" />
    </div>
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-sm" />
      ))}
    </div>
  </div>
);

// Lista de filas (sesiones, dispositivos, etc.).
export const SkeletonList = ({ count = 3, className }) => (
  <div className={cn('space-y-3', className)}>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex items-center justify-between p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm"
      >
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-sm" />
          <div className="space-y-2">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        <Skeleton className="h-6 w-16" />
      </div>
    ))}
  </div>
);

// Página completa por defecto: header + stats + dos tarjetas.
export const SkeletonPage = ({ className }) => (
  <div className={cn('space-y-6 md:space-y-8', className)} data-testid="page-skeleton">
    <SkeletonHeader />
    <SkeletonStatsRow />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <SkeletonCard className="lg:col-span-2" />
      <SkeletonCard rows={2} />
    </div>
  </div>
);

export default SkeletonPage;
